import React, { useCallback, useEffect } from 'react';
import { Slate, Editable, RenderElementProps, RenderLeafProps } from 'slate-react';
import { Descendant } from 'slate';
import { makeStyles } from '@fluentui/react-components';
import { useWordSlateEditor } from './WordSlateEditorContext';

const useStyles = makeStyles({
  editorContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    padding: '20px',
    fontFamily: "'Aptos Display', 'Segoe UI', sans-serif",
    fontSize: '11pt',
    lineHeight: '1.5',
    color: '#000000',
    overflow: 'visible',
    border: 'none',
    outline: 'none',
    '& > div': {
      minHeight: '100%',
    },
  },
  
  editable: {
    minHeight: '100%',
    outline: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    '& p': {
      margin: '0 0 11pt 0',
    },
    '& h1': {
      fontSize: '18pt',
      fontWeight: 'bold',
      margin: '0 0 11pt 0',
    },
    '& h2': {
      fontSize: '14pt',
      fontWeight: 'bold',
      margin: '0 0 11pt 0',
    },
    '& ul, & ol': {
      margin: '0 0 11pt 0',
      paddingLeft: '36pt',
    },
    '& li': {
      margin: '0 0 6pt 0',
    },
  },
  
  // Text formatting styles
  bold: {
    fontWeight: 'bold',
  },
  
  italic: {
    fontStyle: 'italic',
  },
  
  underline: {
    textDecoration: 'underline',
  },
  
  strikethrough: {
    textDecoration: 'line-through',
  },
  
  // Block element styles
  paragraph: {
    margin: '0 0 11pt 0',
  },
  
  headingOne: {
    fontSize: '18pt',
    fontWeight: 'bold',
    margin: '0 0 11pt 0',
    color: '#215e99',
    fontFamily: "'Aptos', 'Segoe UI', sans-serif",
  },
  
  headingTwo: {
    fontSize: '14pt',
    fontWeight: 'bold',
    margin: '0 0 11pt 0',
    color: '#215e99',
    fontFamily: "'Aptos', 'Segoe UI', sans-serif",
  },
  
  bulletedList: {
    margin: '0 0 11pt 0',
    paddingLeft: '36pt',
    listStyleType: 'disc',
  },
  
  numberedList: {
    margin: '0 0 11pt 0',
    paddingLeft: '36pt',
    listStyleType: 'decimal',
  },
  
  listItem: {
    display: 'list-item',
    margin: '0 0 6pt 0',
  },
});

// Component to render block elements
const Element: React.FC<RenderElementProps> = ({ attributes, children, element }) => {
  const styles = useStyles();
  
  // Apply text alignment if specified
  const alignStyle = element.align ? { textAlign: element.align } : {};
  
  switch (element.type) {
    case 'heading-one':
      return <h1 {...attributes} className={styles.headingOne} style={alignStyle}>{children}</h1>;
    case 'heading-two':
      return <h2 {...attributes} className={styles.headingTwo} style={alignStyle}>{children}</h2>;
    case 'bulleted-list':
      return <ul {...attributes} className={styles.bulletedList}>{children}</ul>;
    case 'numbered-list':
      return <ol {...attributes} className={styles.numberedList}>{children}</ol>;
    case 'list-item':
      return <li {...attributes} className={styles.listItem}>{children}</li>;
    default:
      return <p {...attributes} className={styles.paragraph} style={alignStyle}>{children}</p>;
  }
};

// Component to render text with formatting
const Leaf: React.FC<RenderLeafProps> = ({ attributes, children, leaf }) => {
  const styles = useStyles();
  
  let className = '';
  const style: React.CSSProperties = {};
  
  if (leaf.bold) {
    className += ` ${styles.bold}`;
  }
  
  if (leaf.italic) {
    className += ` ${styles.italic}`;
  }
  
  if (leaf.underline) {
    className += ` ${styles.underline}`;
  }
  
  if (leaf.strikethrough) {
    className += ` ${styles.strikethrough}`;
  }
  
  // Apply font size if specified
  if (leaf.fontSize) {
    style.fontSize = `${leaf.fontSize}pt`;
  }
  
  return (
    <span {...attributes} className={className.trim()} style={style}>
      {children}
    </span>
  );
};

interface WordSlateEditorProps {
  placeholder?: string;
  onTextChange?: (text: string) => void;
}

export const WordSlateEditor: React.FC<WordSlateEditorProps> = ({ 
  placeholder = "Start typing your document here...",
  onTextChange
}) => {
  const styles = useStyles();
  const { editor, value, setValue } = useWordSlateEditor();
  
  // Memoize render functions for performance
  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);
  
  // Handle keyboard shortcuts - DISABLED for testing
  const onKeyDown = useCallback((_event: React.KeyboardEvent) => {
    // Keyboard shortcuts temporarily disabled
    return;
  }, []);
  
  // Helper function to extract text from Slate nodes
  const getTextFromNodes = useCallback((nodes: Descendant[]): string => {
    return nodes.map(node => {
      if ('text' in node) {
        return node.text;
      }
      if ('children' in node) {
        return getTextFromNodes(node.children as Descendant[]);
      }
      return '';
    }).join('');
  }, []);
  
  // Handle editor changes to trigger ribbon updates
  const handleChange = useCallback((newValue: Descendant[]) => {
    setValue(newValue);
    // Calculate and report character count
    if (onTextChange) {
      const text = getTextFromNodes(newValue);
      onTextChange(text);
    }
  }, [setValue, onTextChange, getTextFromNodes]);
  
  // Calculate initial character count on mount
  useEffect(() => {
    if (onTextChange && value) {
      const text = getTextFromNodes(value);
      onTextChange(text);
    }
  }, []); // Run only on mount
  
  return (
    <div className={styles.editorContainer}>
      <Slate
        editor={editor}
        initialValue={value}
        onValueChange={handleChange}
      >
        <Editable
          className={styles.editable}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder={placeholder}
          onKeyDown={onKeyDown}
          spellCheck
          autoFocus
        />
      </Slate>
    </div>
  );
};

export default WordSlateEditor;